import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../components/charts';
import {
  Loader2,
  DollarSign,
  Users,
  MousePointerClick,
  Target,
  Filter,
  Download,
  RefreshCw,
  Zap,
  Share2,
  MessageCircle,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';

export default function MetaAds() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [data, setData] = useState({
    summary: {
      spend: 0,
      reach: 0,
      clicks: 0,
      conversions: 0,
      frequency: 0,
      cpc: 0,
      ctr: 0
    },
    performance: {
      daily: {
        labels: [],
        datasets: [{
          label: 'Spend',
          data: [],
          borderColor: 'rgb(24, 119, 242)',
          backgroundColor: 'rgba(24, 119, 242, 0.1)',
          fill: true
        }]
      },
      placements: {
        labels: ['Facebook', 'Instagram', 'Audience Network'],
        datasets: [{
          label: 'Spend',
          data: [0, 0, 0],
          backgroundColor: [
            'rgba(24, 119, 242, 0.8)',
            'rgba(225, 48, 108, 0.8)',
            'rgba(66, 183, 42, 0.8)'
          ]
        }]
      },
      objectives: {
        labels: [],
        datasets: [{
          label: 'Campaigns',
          data: [],
          backgroundColor: [
            'rgba(24, 119, 242, 0.8)',
            'rgba(66, 183, 42, 0.8)',
            'rgba(225, 48, 108, 0.8)',
            'rgba(247, 185, 36, 0.8)'
          ]
        }]
      }
    }
  });

  const fetchData = useCallback(async (force = false) => {
    if (!force && lastFetchTime && Date.now() - lastFetchTime < 5 * 60 * 1000) {
      return;
    }

    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');

      let url = `https://api.glowytics.com/api/meta-ads/dashboard?dateRange=${dateRange}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        url += `&startDate=${fmt(customStartDate)}&endDate=${fmt(customEndDate)}`;
      }
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to fetch data');
      }

      const result = await response.json();
      console.log('Received data:', result);
      setData(result);
      setLastFetchTime(Date.now());

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        fetchData();
      }
    } else {
      fetchData();
    }
  }, [dateRange, customStartDate, customEndDate, fetchData]);

  const performanceMetrics = useMemo(() => {
    return {
      totalSpend: data.campaigns?.reduce((sum, c) => sum + c.spend, 0) || 0,
      totalReach: data.campaigns?.reduce((sum, c) => sum + c.reach, 0) || 0,
      totalClicks: data.campaigns?.reduce((sum, c) => sum + c.clicks, 0) || 0,
      totalConversions: data.campaigns?.reduce((sum, c) => sum + c.conversions, 0) || 0,
    };
  }, [data.campaigns]);

  const handleRefresh = () => {
    fetchData(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-[#1877f2] animate-spin" />
          <p className="text-gray-600 text-lg animate-pulse">
            Loading Meta Ads data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-800">Meta Ads</h1>
        
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value);
              if (e.target.value === 'custom') {
                setCustomStartDate(null);
                setCustomEndDate(null);
              }
            }}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>

          {dateRange === 'custom' && (
            <div className="relative">
              <button
                onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                {customStartDate && customEndDate ? (
                  `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                ) : (
                  'Select Dates'
                )}
              </button>
              {isCustomDateOpen && (
                <div className="absolute right-0 mt-2 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                    <DatePicker
                      selected={customStartDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setCustomStartDate(start);
                        setCustomEndDate(end);
                        if (start && end) {
                          setIsCustomDateOpen(false);
                        }
                      }}
                      startDate={customStartDate}
                      endDate={customEndDate}
                      selectsRange
                      inline
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleRefresh}
            className="p-2 text-gray-600 hover:text-[#1877f2] rounded-xl hover:bg-blue-50"
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          <button className="flex items-center gap-2 px-4 py-2 bg-white border rounded-xl text-gray-600 hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>

          <button className="flex items-center gap-2 px-4 py-2 bg-[#1877f2] text-white rounded-xl hover:bg-[#1864f2]">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Spend</p>
              <p className="text-2xl font-bold mt-2">${performanceMetrics.totalSpend.toLocaleString()}</p>
              <p className="text-sm text-green-600 mt-1">+12.3% vs prev. period</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <DollarSign className="w-6 h-6 text-[#1877f2]" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Reach</p>
              <p className="text-2xl font-bold mt-2">{data.summary.reach.toLocaleString()}</p>
              <p className="text-sm text-green-600 mt-1">+15.8% vs prev. period</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Link Clicks</p>
              <p className="text-2xl font-bold mt-2">{data.summary.clicks.toLocaleString()}</p>
              <p className="text-sm text-green-600 mt-1">+9.2% vs prev. period</p>
            </div>
            <div className="p-3 bg-pink-50 rounded-xl">
              <MousePointerClick className="w-6 h-6 text-[#E1306C]" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Conversions</p>
              <p className="text-2xl font-bold mt-2">{data.summary.conversions.toLocaleString()}</p>
              <p className="text-sm text-green-600 mt-1">+18.5% vs prev. period</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Performance Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Performance */}
        <Card className="p-6 rounded-2xl col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium">Performance Over Time</h2>
            <select className="px-3 py-1.5 border rounded-xl text-sm">
              <option value="spend">Spend</option>
              <option value="reach">Reach</option>
              <option value="clicks">Clicks</option>
              <option value="conversions">Conversions</option>
            </select>
          </div>
          <div className="h-[400px]">
            {data.performance.daily && <LineChart data={data.performance.daily} />}
          </div>
        </Card>

        {/* Placement Distribution */}
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-medium mb-4">Placement Distribution</h2>
          <div className="h-[300px]">
            {data.performance.placements && <DoughnutChart data={data.performance.placements} />}
          </div>
        </Card>

        {/* Campaign Objectives */}
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-medium mb-4">Campaign Objectives</h2>
          <div className="h-[300px]">
            {data.performance.objectives && <BarChart data={data.performance.objectives} />}
          </div>
        </Card>
      </div>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-medium mb-4">Frequency Distribution</h2>
          <div className="h-[250px]">
            {data.performance.frequency && <LineChart data={data.performance.frequency} />}
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-medium mb-4">Cost per Result</h2>
          <div className="h-[250px]">
            {data.performance.cpr && <LineChart data={data.performance.cpr} />}
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-medium mb-4">CTR Analysis</h2>
          <div className="h-[250px]">
            {data.performance.ctr && <LineChart data={data.performance.ctr} />}
          </div>
        </Card>
      </div>

      {/* Campaign Performance Table */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">Campaign Performance</h2>
            <div className="flex gap-4">
              <button className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-xl">
                Filter
              </button>
              <button className="px-4 py-2 text-sm bg-blue-50 text-[#1877f2] hover:bg-blue-100 rounded-xl">
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-4">Campaign</th>
                <th className="text-left py-3 px-4">Objective</th>
                <th className="text-right py-3 px-4">Spend</th>
                <th className="text-right py-3 px-4">Reach</th>
                <th className="text-right py-3 px-4">Impressions</th>
                <th className="text-right py-3 px-4">Clicks</th>
                <th className="text-right py-3 px-4">CTR</th>
                <th className="text-right py-3 px-4">CPC</th>
                <th className="text-right py-3 px-4">Conv.</th>
                <th className="text-right py-3 px-4">Cost/Conv.</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns?.map((campaign) => (
                <tr key={campaign.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium">{campaign.name}</p>
                      <p className="text-sm text-gray-500">{campaign.status}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">{campaign.objective}</td>
                  <td className="text-right py-3 px-4">${campaign.spend.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{campaign.reach.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{campaign.impressions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{campaign.clicks.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{campaign.ctr}%</td>
                  <td className="text-right py-3 px-4">${campaign.cpc}</td>
                  <td className="text-right py-3 px-4">{campaign.conversions}</td>
                  <td className="text-right py-3 px-4">${campaign.costPerConversion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Ad Sets Performance */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">Ad Sets Performance</h2>
            <div className="flex gap-4">
              <button className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-xl">
                Filter
              </button>
              <button className="px-4 py-2 text-sm bg-blue-50 text-[#1877f2] hover:bg-blue-100 rounded-xl">
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-4">Ad Set</th>
                <th className="text-left py-3 px-4">Campaign</th>
                <th className="text-right py-3 px-4">Spend</th>
                <th className="text-right py-3 px-4">Reach</th>
                <th className="text-right py-3 px-4">Impressions</th>
                <th className="text-right py-3 px-4">Clicks</th>
                <th className="text-right py-3 px-4">CTR</th>
                <th className="text-right py-3 px-4">CPC</th>
                <th className="text-right py-3 px-4">Conv.</th>
                <th className="text-right py-3 px-4">Cost/Conv.</th>
              </tr>
            </thead>
            <tbody>
              {data.adSets?.map((adSet) => (
                <tr key={adSet.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <p className="font-medium">{adSet.name}</p>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {adSet.campaignName}
                  </td>
                  <td className="text-right py-3 px-4">${adSet.spend.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{adSet.reach.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{adSet.impressions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{adSet.clicks.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{adSet.ctr}%</td>
                  <td className="text-right py-3 px-4">${adSet.cpc}</td>
                  <td className="text-right py-3 px-4">{adSet.conversions}</td>
                  <td className="text-right py-3 px-4">${adSet.costPerConversion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
} 